import React from "react";
import { Box, Button, ButtonBase, Collapse, Paper, Stack, Typography } from "@mui/material";
import { Add, ExpandMore } from "@mui/icons-material";
import { Task } from "../../../services/taskService";
import { useTaskStore } from "../TaskContext";
import { firstName, TaskSection } from "../taskUtils";
import { CompleteCircle, LinkKind, StaffAvatar, TaskFacts } from "./TaskBits";

const TONES = {
    danger: "#b91c1c",
    warning: "#b45309",
    muted: "text.secondary",
};

interface MyTasksProps {
    sections: TaskSection[];
    collapsed: Set<string>;
    onToggleSection: (key: string) => void;
    activeId: string | null;
    onOpen: (taskId: string) => void;
    onOpenLink: (kind: LinkKind, id: string) => void;
    onNewTask: () => void;
    filtered: boolean;
}

export const MyTasks: React.FC<MyTasksProps> = ({ sections, collapsed, onToggleSection, activeId, onOpen, onOpenLink, onNewTask, filtered }) => {
    const { today } = useTaskStore();
    const openCount = sections.filter((s) => s.key !== "done").reduce((n, s) => n + s.tasks.length, 0);

    if (sections.length === 0) {
        return (
            <Paper variant="outlined" sx={{ py: 8, px: 3, textAlign: "center", borderRadius: 3, borderStyle: "dashed" }}>
                <Typography variant="h6" sx={{ fontWeight: 650 }}>
                    {filtered ? "Nothing matches" : "Your list is clear"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                    {filtered ? "Try a different search." : "Tasks given to you show up here, sorted by when they're due."}
                </Typography>
                {!filtered && (
                    <Button variant="contained" startIcon={<Add />} onClick={onNewTask}>
                        New task
                    </Button>
                )}
            </Paper>
        );
    }

    return (
        <Stack spacing={2.5}>
            {openCount === 0 && !filtered && (
                <Typography variant="body2" color="text.secondary">
                    Nothing open — nice work.
                </Typography>
            )}
            {sections.map((section) => {
                const isCollapsed = collapsed.has(section.key);
                return (
                    <Box key={section.key}>
                        <ButtonBase
                            onClick={() => onToggleSection(section.key)}
                            sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75, px: 0.5, borderRadius: 1 }}
                        >
                            <ExpandMore
                                sx={{ fontSize: 18, color: "text.secondary", transform: isCollapsed ? "rotate(-90deg)" : "none", transition: "transform 150ms" }}
                            />
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: section.tone ? TONES[section.tone] : "text.primary" }}>
                                {section.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                {section.tasks.length}
                            </Typography>
                        </ButtonBase>
                        <Collapse in={!isCollapsed} unmountOnExit>
                            <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: "hidden" }}>
                                {section.tasks.map((task, i) => (
                                    <TaskRow
                                        key={task.task_id}
                                        task={task}
                                        today={today}
                                        active={task.task_id === activeId}
                                        divider={i > 0}
                                        hideDue={section.key === "today"}
                                        onOpen={() => onOpen(task.task_id)}
                                        onOpenLink={onOpenLink}
                                    />
                                ))}
                            </Paper>
                        </Collapse>
                    </Box>
                );
            })}
        </Stack>
    );
};

interface TaskRowProps {
    task: Task;
    today: string;
    active: boolean;
    divider: boolean;
    hideDue?: boolean;
    /** Show who it's assigned to (lists of other people's tasks). */
    showAssignee?: boolean;
    onOpen: () => void;
    onOpenLink: (kind: LinkKind, id: string) => void;
}

export const TaskRow: React.FC<TaskRowProps> = ({ task, today, active, divider, hideDue, showAssignee, onOpen, onOpenLink }) => {
    const { peopleById, meId, rightsFor, patch } = useTaskStore();
    const { canEdit } = rightsFor(task);
    const done = task.status === "DONE";
    const creator = task.created_by_user_id ? peopleById.get(task.created_by_user_id) : undefined;
    const fromSomeoneElse = task.created_by_user_id && task.created_by_user_id !== meId;
    const ref = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (active) ref.current?.scrollIntoView({ block: "nearest" });
    }, [active]);

    return (
        <Stack
            ref={ref}
            direction="row"
            alignItems="flex-start"
            spacing={1.5}
            role="button"
            tabIndex={-1}
            data-task-id={task.task_id}
            onClick={onOpen}
            sx={{
                px: 2,
                py: 1.25,
                cursor: "pointer",
                borderTop: divider ? 1 : 0,
                borderColor: "divider",
                bgcolor: active ? "#eef4f3" : "background.paper",
                boxShadow: active ? "inset 3px 0 0 #18383E" : "none",
                transition: "background-color 120ms",
                "&:hover": { bgcolor: active ? "#eef4f3" : "#f8faf9" },
            }}
        >
            <Box sx={{ pt: 0.25 }}>
                <CompleteCircle done={done} disabled={!canEdit} onToggle={() => patch(task, { status: done ? "TODO" : "DONE" })} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 550,
                        fontSize: 14.5,
                        color: done ? "text.secondary" : "text.primary",
                        textDecoration: done ? "line-through" : "none",
                        wordBreak: "break-word",
                    }}
                >
                    {task.title}
                </Typography>
                <Box sx={{ mt: 0.4 }}>
                    <TaskFacts task={task} today={today} hideDue={hideDue && !task.due_time} onOpenLink={onOpenLink} />
                </Box>
            </Box>
            {showAssignee ? (
                <StaffAvatar person={task.assignee_user_id ? peopleById.get(task.assignee_user_id) : null} userId={task.assignee_user_id} size={26} />
            ) : (
                fromSomeoneElse && (
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0, pt: 0.25 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
                            from {firstName(creator)}
                        </Typography>
                        <StaffAvatar person={creator} userId={task.created_by_user_id} size={22} tooltip={`Assigned by ${firstName(creator)}`} />
                    </Stack>
                )
            )}
        </Stack>
    );
};
